import { useParams, useSearchParams } from "react-router";
import { MediaItemType } from "../../types/mediaItemType";
import useTranslation from "../../../../../packages/locales/translations/util/useTranslation";
import { useArticleById } from "@coworker/app/src/components/RoomSettings/hooks/useArticleById";
import { useMediaById } from "@coworker/app/src/components/RoomSettings/hooks/useMediaById";
import {
  useCountryId,
  useStoreId,
} from "@coworker/app/src/core/auth/useLoggedInUser";
import { useEDSData } from "@coworker/app/src/components/RoomSettings/hooks/useEDSData";
import { useItemInfo } from "@coworker/apprestructured/src/shared/hooks/item/useItemInfo";
import { useCombinationArticleData } from "@coworker/app/src/components/RoomSettings/hooks/useCombinationArticle";
import { useSalesStopData } from "@coworker/app/src/components/RoomSettings/hooks/useSalesStopData";
import { useMediasAndArticleCount } from "@coworker/app/src/components/RoomSettings/hooks/useMediasAndArticleCount";
import React, { useState } from "react";
import LinkbarLoading from "@coworker/apprestructured/src/shared/components/Linkbar/LinkbarLoading";
import Linkbar from "@coworker/apprestructured/src/shared/components/Linkbar/Linkbar";
import FullScreenPopup from "@coworker/apprestructured/src/layout/components/FullScreenPopup/FullScreenPopup";
import { ArticleViewSkeleton } from "@coworker/app/src/components/RoomSettings/Skeletons/ArticleViewSkeleton";
import LocalCombinationSubArticlePanel from "@coworker/app/src/components/RoomSettings/Molecules/LocalCombinationSubArticlePanel";
import {
  isArticleInCombination,
  isCombinationArticleValid,
} from "@coworker/app/src/components/RoomSettings/Helpers/articleHelpers";
import { ItemListViewItem } from "../../components/ItemListViewItem/ItemListViewItem";
import { ArticleListItemMobile } from "@coworker/app/src/components/RoomSettings/styles/styles";
import { ItemCardRenderType } from "@coworker/apprestructured/src/shared/types/itemCardRenderType";
import { MediaPlacementSection } from "../../components/MediaPlacementSection/MediaPlacementSection";
import { AboutArticleSection } from "../../components/AboutArticleSection/AboutArticleSection";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { getMediaItemBadges } from "../../helpers/getMediaItemBadges";
import { ItemListViewItemCard } from "../../components/item-cards/ItemListViewItemCard/ItemListViewItemCard";
import { MediaItemViewType } from "../../types/mediaItemViewType";
import { Article } from "@coworker/app/src/components/RoomSettings/types/article";
import { MediaPlacementsModal } from "../../components/MediaPlacementSection/MediaPlacementsModal/MediaPlacementsModal";
import { useAllTaskTypes } from "@coworker/apprestructured/src/task-management/services/useTaskTypes";
import {
  TaskTypeNames,
  MEDIA_TASK_TYPE_NAMES,
} from "@coworker/apprestructured/src/task-management/constants/TaskTypeNames";
import { MediaTasksSection } from "../../components/MediaTasksSection/MediaTasksSection";
import { useMediaTasksDataForItem } from "../../components/MediaTasksSection/hooks/useMediaTasksDataForItem";
import { useMediaItemsWithTasks } from "../../components/MediaTasksSection/hooks/useMediaItemsWithTasks";
import useFlag, { FLAGS } from "@coworker/app/src/hooks/useFlag";
import { useMemo } from "react";
import { use4AK1ArticleData } from "@coworker/app/src/components/RoomSettings/hooks/use4AK1ArticleData";
import { useItemTagsData } from "@coworker/app/src/components/RoomSettings/hooks/useItemTagsData";

export const MediaArticleInformationPageGroups = () => {
  const { articleId } = useParams();
  const [searchParams] = useSearchParams();

  const mediaItemType =
    (searchParams.get("media-item-type") as MediaItemType) || undefined;

  const { t } = useTranslation();

  // Use useArticleById instead of useArticlesByProductArticleId
  const { data: article, isLoading: isArticleLoading } =
    useArticleById(articleId);

  // Get mediaId and productArticleId from loaded article
  const mediaId = article?.mediaId;
  const productArticleId = article?.productArticleId;

  const { data: mediaResponse, isLoading: isMediaLoading } =
    useMediaById(mediaId);

  const countryId = useCountryId();
  const { data: edsData } = useEDSData(countryId, productArticleId || "");
  const enableNewMediaBadges = useFlag(FLAGS.ENABLE_NEW_MEDIA_BADGES);
  const { data: array4AK1Badges } = use4AK1ArticleData(
    productArticleId,
    countryId,
    enableNewMediaBadges
  );
  const { data: itemTags } = useItemTagsData(
    productArticleId,
    enableNewMediaBadges
  );
  const { data: itemInfo, isLoading: isItemInfoLoading } = useItemInfo(
    productArticleId ?? ""
  );

  const { data: taskTypes } = useAllTaskTypes();

  function getTaskTypeId(taskTypeName: TaskTypeNames) {
    return taskTypes?.find((type) => type.name === taskTypeName)?.id;
  }

  const storeId = useStoreId();
  const isMediaSubItem = mediaItemType === MediaItemType.LC_SUBITEM;

  const { data: combinationArticle } = useCombinationArticleData(
    articleId,
    isMediaSubItem
  );

  const enableMediaV2Tasks = useFlag(FLAGS.ENABLE_MEDIA_V2_TASKS);

  const mediaTaskTypeIds = useMemo(
    () =>
      (taskTypes || [])
        .filter((tt) =>
          (MEDIA_TASK_TYPE_NAMES as readonly string[]).includes(tt.name)
        )
        .map((tt) => tt.id),
    [taskTypes]
  );

  // Fetch v2 tasks for this specific media (to check if THIS article has tasks)
  const { data: v2TasksArray = [] } = useMediaItemsWithTasks(
    enableMediaV2Tasks ? mediaId : undefined,
    mediaTaskTypeIds
  );

  // Check if THIS specific article (article_id + item_no) has v2 tasks
  const hasV2TasksForArticle = useMemo(() => {
    if (!article?.id || !article?.productArticleId) return false;
    return v2TasksArray.some(
      ({ article_id, item_no }) =>
        article_id === article.id && item_no === article.productArticleId
    );
  }, [v2TasksArray, article]);

  // Fetch all task data (v1 + v2) for this item across ALL medias
  const taskData = useMediaTasksDataForItem(
    mediaResponse?.media?.storeId || "",
    article?.productArticleId || ""
  );

  const { data: salesStopData } = useSalesStopData(
    storeId,
    article?.productArticleId ?? ""
  );

  const {
    data: mediasAndArticleCount,
    isLoading: isMediasAndArticleCountLoading,
  } = useMediasAndArticleCount(productArticleId, mediaResponse?.media.storeId);

  const hasMediasLoadError =
    !isMediasAndArticleCountLoading && mediasAndArticleCount == null;

  const filteredMediasAndArticleCount = mediasAndArticleCount?.filter(
    (media) => !media.parentArticleCombinationNumber
  );

  const taskTypeId = getTaskTypeId(
    TaskTypeNames.MEDIA_MAINTENANCE_MULTIPLE_MEDIA
  );

  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);

  const totalQuantityInPieces =
    mediasAndArticleCount
      ?.filter((media) => media.mediaId !== mediaId)
      .reduce((acc, media) => acc + media.nbrArticles, 0) || 0;

  // For Groups: badge shows only if v1 OR (v2 with BOTH article_id AND item_no match)
  const hasV1Tasks = taskData.v1TaskLinks.length > 0;
  const hasOngoingTasksForThisArticle = hasV1Tasks || hasV2TasksForArticle;

  const badges = React.useMemo(() => {
    const shouldShowProductBadges = false;
    if (article && mediaResponse?.media) {
      return getMediaItemBadges(
        article?.id,
        [article?.id],
        t,
        hasOngoingTasksForThisArticle,
        salesStopData?.find(
          (salesStop) => salesStop.productArticleId === article.productArticleId
        ) ?? null,
        mediaResponse?.media,
        shouldShowProductBadges,
        enableNewMediaBadges ? (array4AK1Badges ?? []) : [],
        enableNewMediaBadges ? (itemTags ?? []) : []
      );
    }
    return [];
  }, [
    article,
    array4AK1Badges,
    enableNewMediaBadges,
    itemTags,
    mediaResponse?.media,
    hasOngoingTasksForThisArticle,
    salesStopData,
    t,
  ]);

  const linkbar = React.useMemo(() => {
    if (!mediaResponse?.media.name || !mediaResponse?.media.id) {
      return <LinkbarLoading />;
    }

    const linkBarItems = [
      {
        label: t("mediaMaintenanceString"),
        to: "/mediamaintenance",
      },
      {
        label: mediaResponse.media.name,
        to: `/mediamaintenance/tabs/${mediaResponse.media.id}?tab=article-list-tab`,
      },
    ];

    if (mediaResponse.parent?.name) {
      linkBarItems.splice(1, 0, {
        label: mediaResponse.parent.name,
        to: `/mediamaintenance/tabs/${mediaResponse.parent.id}?tab=article-list-tab`,
      });
    }

    return (
      <Linkbar
        items={linkBarItems}
        currentLabel={article?.itemName ?? ""}
        currentAsH1
      />
    );
  }, [article?.itemName, mediaResponse, t]);

  const itemCardContent = React.useMemo(() => {
    if (!article || !mediaResponse) return null;
    return (
      <ItemListViewItemCard<Article>
        edsData={edsData?.[0] || null}
        mediaItemType={mediaItemType}
        showActions={false}
        itemCardRenderType={ItemCardRenderType.static}
        isItemInGroups={false}
        badges={badges}
        article={article}
        media={mediaResponse.media}
        mediaItemViewType={MediaItemViewType.SIMPLE_ITEM_VIEW}
        itemInfo={itemInfo ?? null}
        itemInfoLoading={isItemInfoLoading}
        shouldDisplayQuantity={true}
        totalQuantityPieces={totalQuantityInPieces}
      />
    );
  }, [
    article,
    mediaResponse,
    edsData,
    mediaItemType,
    badges,
    itemInfo,
    isItemInfoLoading,
    totalQuantityInPieces,
  ]);

  // Handle loading cascade (article first, then media)
  if (isArticleLoading)
    return (
      <FullScreenPopup noPadding linkbar={<LinkbarLoading />}>
        <ArticleViewSkeleton />
      </FullScreenPopup>
    );

  if (!article) return null;

  if (isMediaLoading)
    return (
      <FullScreenPopup noPadding linkbar={linkbar}>
        <ArticleViewSkeleton />
      </FullScreenPopup>
    );

  if (!mediaResponse) return null;

  return (
    <FullScreenPopup
      noPadding
      linkbar={linkbar}
      actionBarContent={
        <FixaButton
          type="primary"
          fluid
          text={t("createString")}
          onClick={() => setShowCreateTaskModal(true)}
          disabled={!taskTypeId || isMediasAndArticleCountLoading}
        />
      }
    >
      {(isArticleInCombination(article) || isMediaSubItem) && (
        <LocalCombinationSubArticlePanel
          parentArticle={
            isCombinationArticleValid(combinationArticle)
              ? combinationArticle
              : undefined
          }
        />
      )}
      <div>
        <ArticleListItemMobile>
          <ItemListViewItem
            article={article}
            media={mediaResponse.media}
            hasOngoingTasks={hasOngoingTasksForThisArticle}
            edsData={edsData?.[0] || null}
            itemCardRenderType={ItemCardRenderType.static}
            salesStopData={salesStopData?.[0] || null}
            itemInfo={itemInfo ?? null}
            itemInfoLoading={isItemInfoLoading}
            mediaItemType={mediaItemType ?? MediaItemType.REGULAR_ITEM}
            badges4AK1={enableNewMediaBadges ? (array4AK1Badges ?? []) : []}
            itemTags={enableNewMediaBadges ? (itemTags ?? []) : []}
          />
        </ArticleListItemMobile>
      </div>
      {isMediasAndArticleCountLoading ? (
        <ArticleViewSkeleton />
      ) : (
        mediasAndArticleCount &&
        itemCardContent && (
          <MediaPlacementSection
            itemCardContent={itemCardContent}
            media={mediaResponse.media}
            mediasAndArticleCount={mediasAndArticleCount}
          />
        )
      )}
      {!taskData.isLoading && <MediaTasksSection data={taskData} />}
      <AboutArticleSection
        hfb={article.hfb}
        itemNoAndType={{
          no: article?.productArticleId ?? "",
          type: article?.productArticleType ?? "",
        }}
      />
      <MediaPlacementsModal
        itemNoAndType={{
          no: article?.productArticleId ?? "",
          type: article?.productArticleType ?? "",
        }}
        showModal={showCreateTaskModal}
        onClose={() => setShowCreateTaskModal(false)}
        mediasAndArticleCount={filteredMediasAndArticleCount ?? []}
        title={t("createTaskString")}
        showCheckBox={true}
        showFooter={true}
        taskTypeId={taskTypeId}
        mediaId={mediaId}
        hasMediasLoadError={hasMediasLoadError}
      />
    </FullScreenPopup>
  );
};
